import type { ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchOrders, fetchPositions, fetchTrades } from '../api/endpoints';
import { queryKeys } from '../api/query-keys';
import type { Order, Trade } from '../api/schemas';
import { EmptyState } from '../components/EmptyState';
import { Pnl, Price, Quantity, Usdt } from '../components/Money';
import { PositionCard } from '../components/PositionCard';
import { QueryState } from '../components/QueryState';
import { ReasonText } from '../components/ReasonText';
import { Section } from '../components/Section';
import { StatusPill } from '../components/StatusPill';
import { TableScroll } from '../components/TableScroll';
import {
  ORDER_KIND_LABEL,
  ORDER_STATUS_LABEL,
  ORDER_STATUS_TONE,
  ORDER_STATUS_UNKNOWN_HINT,
  SIDE_LABEL,
} from '../domain/labels';
import { formatDateTime } from '../lib/datetime';

/**
 * Раздел 3 — «Позиции и ордера».
 *
 * The position and its protective orders, then the closed-trade history. Both
 * tables are wide, so each scrolls inside its own container: at 390px the page
 * itself still has no horizontal scrollbar.
 */
export function PositionsPage(): ReactElement {
  const positionQuery = useQuery({
    queryKey: queryKeys.position,
    queryFn: ({ signal }) => fetchPositions(signal),
  });
  const ordersQuery = useQuery({
    queryKey: queryKeys.orders,
    queryFn: ({ signal }) => fetchOrders(signal),
  });
  const tradesQuery = useQuery({
    queryKey: queryKeys.trades,
    queryFn: ({ signal }) => fetchTrades(signal),
  });

  return (
    <>
      <div className="page-head">
        <h1>Позиции и ордера</h1>
        <span className="page-head__hint">
          Открытая позиция, защитные ордера и история закрытых сделок.
        </span>
      </div>

      <div className="stack">
        <Section title="Открытая позиция">
          <QueryState query={positionQuery} what="позицию">
            {(data) => <PositionCard position={data.items[0] ?? null} />}
          </QueryState>
        </Section>

        <Section title="Ордера" hint="Все ордера текущей и последней сессии со статусом площадки.">
          <QueryState query={ordersQuery} what="список ордеров">
            {(data) =>
              data.items.length === 0 ? (
                <EmptyState>Ордеров нет.</EmptyState>
              ) : (
                <OrdersTable orders={data.items} />
              )
            }
          </QueryState>
        </Section>

        <Section title="История сделок" hint="Закрытые сделки с итоговым результатом и причинами.">
          <QueryState query={tradesQuery} what="историю сделок">
            {(data) =>
              data.items.length === 0 ? (
                <EmptyState>Закрытых сделок пока нет.</EmptyState>
              ) : (
                <TradesTable trades={data.items} />
              )
            }
          </QueryState>
        </Section>
      </div>
    </>
  );
}

function OrdersTable({ orders }: { orders: readonly Order[] }): ReactElement {
  return (
    <TableScroll label="Таблица ордеров, прокручивается по горизонтали">
      <table className="data-table">
        <caption>Ордера по текущему инструменту</caption>
        <thead>
          <tr>
            <th scope="col">Назначение</th>
            <th scope="col">Сторона</th>
            <th scope="col">Статус</th>
            <th scope="col" className="num">
              Объём
            </th>
            <th scope="col" className="num">
              Исполнено
            </th>
            <th scope="col" className="num">
              Цена
            </th>
            <th scope="col" className="num">
              Средняя цена
            </th>
            <th scope="col">Создан</th>
            <th scope="col">Обновлён</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((order) => (
            <tr key={order.orderId}>
              <td>{ORDER_KIND_LABEL[order.kind]}</td>
              <td>{SIDE_LABEL[order.side]}</td>
              <td>
                <StatusPill tone={ORDER_STATUS_TONE[order.status]} srPrefix="Статус ордера">
                  {ORDER_STATUS_LABEL[order.status]}
                </StatusPill>
                {order.status === 'UNKNOWN' && (
                  <p className="field__hint">{ORDER_STATUS_UNKNOWN_HINT}</p>
                )}
              </td>
              <td className="num">
                <Quantity value={order.quantity} />
              </td>
              <td className="num">
                <Quantity value={order.filledQuantity} />
              </td>
              <td className="num">
                <Price value={order.price} />
              </td>
              <td className="num">
                <Price value={order.averageFillPrice} />
              </td>
              <td>{formatDateTime(order.createdAt)}</td>
              <td>{formatDateTime(order.updatedAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableScroll>
  );
}

function TradesTable({ trades }: { trades: readonly Trade[] }): ReactElement {
  return (
    <TableScroll label="Таблица сделок, прокручивается по горизонтали">
      <table className="data-table">
        <caption>Закрытые и текущие сделки</caption>
        <thead>
          <tr>
            <th scope="col">Открыта</th>
            <th scope="col">Закрыта</th>
            <th scope="col">Сторона</th>
            <th scope="col" className="num">
              Вход
            </th>
            <th scope="col" className="num">
              Выход
            </th>
            <th scope="col" className="num">
              Объём
            </th>
            <th scope="col" className="num">
              Маржа
            </th>
            <th scope="col" className="num">
              PnL до комиссий
            </th>
            <th scope="col" className="num">
              PnL после комиссий
            </th>
            <th scope="col" className="num">
              Комиссии
            </th>
            <th scope="col" className="num">
              Funding
            </th>
            <th scope="col" className="wrap">
              Причина входа
            </th>
            <th scope="col" className="wrap">
              Причина выхода
            </th>
          </tr>
        </thead>
        <tbody>
          {trades.map((trade) => (
            <tr key={trade.tradeId}>
              <td>{formatDateTime(trade.openedAt)}</td>
              <td>{trade.closedAt === null ? 'в позиции' : formatDateTime(trade.closedAt)}</td>
              <td>{SIDE_LABEL[trade.side]}</td>
              <td className="num">
                <Price value={trade.entryPrice} />
              </td>
              <td className="num">
                <Price value={trade.exitPrice} />
              </td>
              <td className="num">
                <Quantity value={trade.quantity} />
              </td>
              <td className="num">
                <Usdt value={trade.marginAmount} />
              </td>
              <td className="num">
                <Pnl value={trade.grossPnl} />
              </td>
              <td className="num">
                <Pnl value={trade.netPnl} />
              </td>
              <td className="num">
                <Usdt value={trade.fees} />
              </td>
              <td className="num">
                <Usdt value={trade.funding} />
              </td>
              <td className="wrap">
                <ReasonText code={trade.entryReasonCode} />
              </td>
              <td className="wrap">
                {trade.exitReasonCode === null ? (
                  <span className="muted">—</span>
                ) : (
                  <ReasonText code={trade.exitReasonCode} />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableScroll>
  );
}
